import analysisApiClient, { AnalysisApiClient } from '../api/AnalysisApiClient';
import { ANALYSIS_GATEWAY_ENDPOINT } from '../types/contracts';
import {
    normalizeExecutionId,
    normalizeReferenceCode,
    normalizeRequestId,
    normalizeSubjectId
} from '../types/identifiers';
import { normalizeSubjectResponse } from '../subject/subjectMapper';
import { normalizeAnalysisPayload } from '../subject/analysisPayloadMapper';
import { normalizeNextActionPayload } from '../types/nextAction';

const toTrimmedString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const toBoolean = (value) => value === true;

const normalizeHeaderValue = (headers = {}, headerName = '') => {
    if (!headers || typeof headers !== 'object' || !headerName) {
        return null;
    }

    const normalizedHeaderName = String(headerName).trim().toLowerCase();
    const matchedKey = Object.keys(headers).find((key) => String(key).trim().toLowerCase() === normalizedHeaderName);
    if (!matchedKey) {
        return null;
    }

    return toTrimmedString(headers[matchedKey]);
};

const normalizeCollectionResponse = (response = {}) => {
    const items = Array.isArray(response?.results)
        ? response.results
        : (Array.isArray(response?.items) ? response.items : []);

    return {
        items,
        total: Number(response?.count ?? response?.total ?? items.length ?? 0),
        policyVersion: toTrimmedString(response?.policy_version),
        raw: response
    };
};

const normalizeCapabilityCatalogResponse = ({
    data: response = {},
    status = 200,
    headers = {}
} = {}) => {
    const normalizedStatus = Number(status) || 200;
    const cacheStatus = toTrimmedString(response?.cache_status);
    const notModified = cacheStatus === 'not_modified';
    const fingerprintFromHeader = normalizeHeaderValue(headers, 'X-Photoscope-Resource-Fingerprint');
    const fingerprintFromBody = toTrimmedString(response?.fingerprint);
    const fingerprint = fingerprintFromHeader || fingerprintFromBody;
    const policyFingerprint = normalizeHeaderValue(headers, 'X-Photoscope-Policy-Fingerprint');

    console.log('[AnalysisGateway] normalizeCapabilityCatalogResponse headers', {
        headerKeys: Object.keys(headers || {}),
        accessibleHeaderCount: Object.keys(headers || {}).length,
        fingerprintFromHeader,
        fingerprintFromBody,
        finalFingerprint: fingerprint,
        policyFingerprint,
        cacheStatus,
        notModified
    });

    if (notModified) {
        return {
            items: [],
            total: 0,
            policyVersion: toTrimmedString(response?.policy_version),
            runCode: null,
            tiers: [],
            tierBenefits: {},
            defaultBundleByTier: {},
            fingerprint,
            policyFingerprint,
            cacheStatus,
            notModified: true,
            status: normalizedStatus,
            raw: response
        };
    }

    const items = Array.isArray(response?.capabilities)
        ? response.capabilities
        : (Array.isArray(response?.items) ? response.items : []);

    return {
        items,
        total: Number(response?.count ?? response?.total ?? items.length ?? 0),
        policyVersion: toTrimmedString(response?.policy_version),
        runCode: toTrimmedString(response?.run_code),
        tiers: Array.isArray(response?.tiers) ? response.tiers : [],
        tierBenefits: response?.tier_benefits || {},
        defaultBundleByTier: response?.default_bundle_by_tier || {},
        fingerprint,
        policyFingerprint,
        cacheStatus,
        notModified: false,
        status: normalizedStatus,
        raw: response
    };
};

const normalizeQuoteResponse = (response = {}) => ({
    operationType: toTrimmedString(response?.operation_type),
    tier: toTrimmedString(response?.tier),
    balance: response?.balance || null,
    quotedItems: Array.isArray(response?.quoted_items) ? response.quoted_items : [],
    confirmationRequiredCapabilities: Array.isArray(response?.confirmation_required_capabilities)
        ? response.confirmation_required_capabilities
        : [],
    totalCreditsRequired: Number(response?.total_credits_required ?? 0),
    sufficientCredits: toBoolean(response?.sufficient_credits),
    sufficientToContinue: toBoolean(response?.sufficient_to_continue),
    paymentMaterializationPending: toBoolean(response?.payment_materialization_pending),
    nextAction: normalizeNextActionPayload(response?.next_action),
    quotaPreview: response?.quota_preview || null,
    continueBlockingReason: toTrimmedString(response?.continue_blocking_reason),
    reasonCode: toTrimmedString(response?.continue_blocking_reason),
    raw: response
});

const normalizeRunResponse = (response = {}) => ({
    subjectId: normalizeSubjectId(response?.subject_id),
    executionId: normalizeExecutionId(response?.execution_id),
    requestId: normalizeRequestId(response?.request_id),
    referenceCode: normalizeReferenceCode(response?.reference_code),
    outputLanguage: toTrimmedString(response?.output_language || response?.outputLanguage),
    requestedOutputLanguage: toTrimmedString(response?.requested_output_language || response?.requestedOutputLanguage),
    outputLanguageResolution: toTrimmedString(response?.output_language_resolution || response?.outputLanguageResolution),
    outputLanguageResolutionReason: toTrimmedString(response?.output_language_resolution_reason || response?.outputLanguageResolutionReason),
    aiDispatchState: toTrimmedString(response?.ai_dispatch_state),
    aiResultId: toTrimmedString(response?.ai_result_id),
    currentVisibleResultId: toTrimmedString(response?.current_visible_result_id),
    resultAccessMode: toTrimmedString(response?.result_access_mode),
    aiEffectiveSourceKind: toTrimmedString(response?.ai_effective_source_kind),
    aiEffectiveSourceHashId: toTrimmedString(response?.ai_effective_source_hash_id),
    chargedTotal: response?.charged_total ?? null,
    chargedItems: Array.isArray(response?.charged_items) ? response.charged_items : [],
    grantedCapabilities: Array.isArray(response?.granted_capabilities) ? response.granted_capabilities : [],
    fulfillmentStatus: toTrimmedString(response?.fulfillment_status),
    upsellHints: response?.upsell_hints || null,
    entitlementContext: response?.entitlement_context || null,
    raw: response
});

const unwrapTransportResponse = (responseOrMeta = {}) => {
    const hasMetaEnvelope = hasOwn(responseOrMeta, 'data') &&
        (hasOwn(responseOrMeta, 'status') || hasOwn(responseOrMeta, 'headers'));

    return {
        response: hasMetaEnvelope ? (responseOrMeta?.data || {}) : (responseOrMeta || {}),
        status: hasMetaEnvelope ? (Number(responseOrMeta?.status) || null) : null
    };
};

const normalizeAiJobResponse = (responseOrMeta = {}) => {
    const { response, status } = unwrapTransportResponse(responseOrMeta);
    const outputLanguage = toTrimmedString(
        response?.meta?.output_language ||
        response?.meta?.outputLanguage ||
        response?.output_language ||
        response?.outputLanguage
    );

    return {
        jobId: toTrimmedString(response?.job_id || response?.id),
        subjectId: normalizeSubjectId(response?.subject_id),
        executionId: normalizeExecutionId(response?.execution_id),
        requestId: normalizeRequestId(response?.request_id),
        retryRequestId: normalizeRequestId(response?.retry_request_id),
        status: toTrimmedString(response?.status),
        aiDispatchState: toTrimmedString(response?.ai_dispatch_state),
        aiResultId: toTrimmedString(response?.ai_result_id),
        retryable: response?.retryable === true,
        retryWithSameRequestId: response?.retry_with_same_request_id === true,
        requestable: response?.requestable === true,
        failureCode: toTrimmedString(response?.failure_code || response?.failureCode),
        recoveryMode: toTrimmedString(response?.recovery_mode || response?.recoveryMode),
        nextAction: normalizeNextActionPayload(response?.next_action, {
            jobId: toTrimmedString(response?.job_id || response?.id),
            subjectId: normalizeSubjectId(response?.subject_id),
            executionId: normalizeExecutionId(response?.execution_id),
            retryRequestId: normalizeRequestId(response?.retry_request_id)
        }),
        httpStatus: status,
        meta: response?.meta || null,
        analysisMarkdown: response?.analysis_markdown || null,
        analysisPayload: normalizeAnalysisPayload(response?.analysis_payload || response?.analysisPayload),
        outputLanguage,
        output_language: outputLanguage,
        raw: response
    };
};

const UPLOAD_AUTHORIZATION_ROLES = ['original', 'processed'];

const createInvalidAuthorizationResponseError = (detail) => {
    const error = new Error(
        `[AnalysisGateway] invalid upload authorization response: ${detail}`
    );
    error.code = 'invalid_upload_authorization_response';
    error.status = null;
    error.responseData = null;
    return error;
};

const readStrictBoolean = (item, field, role) => {
    const value = item?.[field];
    if (typeof value !== 'boolean') {
        throw createInvalidAuthorizationResponseError(
            `role "${role}" field "${field}" must be a boolean, received ${typeof value}`
        );
    }
    return value;
};

const readRequiredString = (item, field, role) => {
    const value = item?.[field];
    if (typeof value !== 'string' || value.trim() === '') {
        throw createInvalidAuthorizationResponseError(
            `role "${role}" field "${field}" must be a non-empty string`
        );
    }
    return value;
};

// §4.2 / 后端 SoR：expires_at 必须为**严格**的 RFC 3339 UTC 时间戳（仅以 `Z` 或 `+00:00` 表示零偏移）。
// 先以严格格式正则校验（拒绝 `+0000`、空格替代 `T`、无冒号偏移、尾随垃圾字符），
// 再以 Date.UTC 构造并回读组件，确保日历确实有效（避免 02-30 / 13 月被 Date 自动跨月滚动导致误判）。
// 不依赖 Date.parse 的宽松解析，因为后者会忽略尾随字符并自动滚动非法日期。
const RFC_3339_UTC_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?(Z|\+00:00)$/;

const isRfc3339Utc = (value) => {
    if (typeof value !== 'string' || value.trim() === '') {
        return false;
    }
    const match = RFC_3339_UTC_RE.exec(value);
    if (!match) {
        return false;
    }
    const [, year, month, day, hour, minute, second] = match;
    const date = new Date(Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
    ));
    return (
        date.getUTCFullYear() === Number(year) &&
        date.getUTCMonth() === Number(month) - 1 &&
        date.getUTCDate() === Number(day) &&
        date.getUTCHours() === Number(hour) &&
        date.getUTCMinutes() === Number(minute) &&
        date.getUTCSeconds() === Number(second)
    );
};

const normalizeUploadAuthorizationItem = (role, rawItem) => {
    if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) {
        throw createInvalidAuthorizationResponseError(
            `role "${role}" must be an object`
        );
    }

    const objectUploadRequired = readStrictBoolean(rawItem, 'object_upload_required', role);
    const runAssetMaterialRequired = readStrictBoolean(rawItem, 'run_asset_material_required', role);

    // 三种合法状态：false/false、false/true、true/true；true/false 非法。
    if (objectUploadRequired && !runAssetMaterialRequired) {
        throw createInvalidAuthorizationResponseError(
            `role "${role}" has illegal state object_upload_required=true with run_asset_material_required=false`
        );
    }

    const resourceKey = readRequiredString(rawItem, 'resource_key', role);
    const fileType = readRequiredString(rawItem, 'file_type', role);

    const normalized = {
        objectUploadRequired,
        runAssetMaterialRequired,
        resourceKey,
        fileType
    };

    if (objectUploadRequired) {
        const uploadUrl = readRequiredString(rawItem, 'upload_url', role);
        const expiresAt = readRequiredString(rawItem, 'expires_at', role);
        if (!rawItem?.required_headers || typeof rawItem.required_headers !== 'object' || Array.isArray(rawItem.required_headers)) {
            throw createInvalidAuthorizationResponseError(
                `role "${role}" object_upload_required=true requires object "required_headers"`
            );
        }

        // §4.2 / 后端 SoR：required_headers 必须为 object of string，且固定头 Content-Type、
        // If-None-Match 必须存在；否则 malformed success response 在边界 fail-closed。
        const REQUIRED_HEADER_KEYS = ['Content-Type', 'If-None-Match'];
        for (const [headerKey, headerValue] of Object.entries(rawItem.required_headers)) {
            if (typeof headerValue !== 'string') {
                throw createInvalidAuthorizationResponseError(
                    `role "${role}" required_headers["${headerKey}"] must be a string`
                );
            }
        }
        for (const headerKey of REQUIRED_HEADER_KEYS) {
            if (!hasOwn(rawItem.required_headers, headerKey)) {
                throw createInvalidAuthorizationResponseError(
                    `role "${role}" required_headers missing fixed header "${headerKey}"`
                );
            }
        }

        // §4.2 / 后端 SoR：expires_at 必须为可解析的 RFC 3339 UTC 时间戳。
        if (!isRfc3339Utc(expiresAt)) {
            throw createInvalidAuthorizationResponseError(
                `role "${role}" expires_at must be a valid RFC 3339 UTC timestamp`
            );
        }

        normalized.uploadUrl = uploadUrl;
        normalized.requiredHeaders = rawItem.required_headers;
        normalized.expiresAt = expiresAt;
    } else {
        // §4.2：object_upload_required=false 时不应出现签名字段，严格 fail-closed 拒绝多余签名。
        const unexpectedSignatureFields = ['upload_url', 'required_headers', 'expires_at']
            .filter((field) => hasOwn(rawItem, field));
        if (unexpectedSignatureFields.length > 0) {
            throw createInvalidAuthorizationResponseError(
                `role "${role}" object_upload_required=false must not include ${unexpectedSignatureFields.join(', ')}`
            );
        }
    }

    return normalized;
};

export const normalizeRunAssetUploadAuthorization = (response = {}, requestedRoles = ['original']) => {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
        throw createInvalidAuthorizationResponseError('response must be an object');
    }

    // requestedRoles 必须由调用方依据本次 request payload 传入：response 顶层 role 集合
    // 必须与请求严格一致（方案 §5.1.1「缺少 requested role」、后端 SoR「请求含 processed 时
    // 响应同样包含 processed，省略时响应也省略」）。original 为契约必填角色，始终纳入；
    // processed 仅当本次请求携带时纳入。
    const requestedSet = (Array.isArray(requestedRoles) && requestedRoles.length > 0)
        ? requestedRoles
        : ['original'];

    const normalized = {};

    // 1) 每个请求的 role 都必须出现在 response 中，否则 malformed success response fail-closed。
    for (const role of requestedSet) {
        if (!hasOwn(response, role)) {
            throw createInvalidAuthorizationResponseError(
                `response is missing requested role "${role}"`
            );
        }
        normalized[role] = normalizeUploadAuthorizationItem(role, response[role]);
    }

    // 2) response 不得包含任何未请求的 role（含 original-only 请求被多返回 processed）。
    const unexpectedRoles = Object.keys(response).filter(
        (key) => !requestedSet.includes(key)
    );
    if (unexpectedRoles.length > 0) {
        throw createInvalidAuthorizationResponseError(
            `response contains unexpected role(s): ${unexpectedRoles.join(', ')}`
        );
    }

    return normalized;
};

export class AnalysisGateway {
    constructor({ apiClient = analysisApiClient } = {}) {
        this.apiClient = apiClient instanceof AnalysisApiClient || (apiClient?.get && apiClient?.post)
            ? apiClient
            : analysisApiClient;
    }

    async getReferenceCatalogs(params = {}) {
        const response = await this.apiClient.get(ANALYSIS_GATEWAY_ENDPOINT.REFERENCE_CATALOGS, { params });
        return normalizeCollectionResponse(response);
    }

    async getCapabilityCatalogs(params = {}) {
        const requestHeaders = { ...(params?.headers || {}) };
        const requestParams = params?.params
            ? { ...(params.params || {}) }
            : Object.fromEntries(
                Object.entries(params || {}).filter(([key]) => key !== 'headers')
            );
        const response = await this.apiClient.get(ANALYSIS_GATEWAY_ENDPOINT.CAPABILITY_CATALOGS, {
            params: requestParams,
            headers: requestHeaders,
            includeMeta: true
        });
        return normalizeCapabilityCatalogResponse(response);
    }

    async quote(payload = {}) {
        const response = await this.apiClient.post(ANALYSIS_GATEWAY_ENDPOINT.QUOTES, payload);
        return normalizeQuoteResponse(response);
    }

    async resolveRunAssets(payload = {}) {
        const response = await this.apiClient.post(ANALYSIS_GATEWAY_ENDPOINT.RUN_ASSET_RESOLUTIONS, payload);
        return {
            originalImageResolution: response?.original_image_resolution || null,
            processedImageResolution: response?.processed_image_resolution || null,
            aiSourceResolution: response?.ai_source_resolution || null,
            raw: response
        };
    }

    async authorizeRunAssetUploads(payload = {}) {
        const response = await this.apiClient.post(
            ANALYSIS_GATEWAY_ENDPOINT.RUN_ASSET_UPLOAD_AUTHORIZATIONS,
            payload
        );
        // 依据本次 request payload 推导 requested roles：original 必填恒纳入，
        // processed 仅当请求携带时纳入，供 normalizer 做严格集合匹配（A.2 P1 修复）。
        const requestedRoles = UPLOAD_AUTHORIZATION_ROLES.filter(
            (role) => role === 'original' || (hasOwn(payload, role) && payload[role])
        );
        return normalizeRunAssetUploadAuthorization(response, requestedRoles);
    }

    async createRun(payload = {}) {
        const response = await this.apiClient.post(ANALYSIS_GATEWAY_ENDPOINT.RUNS, payload);
        return normalizeRunResponse(response);
    }

    async getSubject(subjectId, params = {}) {
        const normalizedSubjectId = normalizeSubjectId(subjectId);
        if (!normalizedSubjectId) {
            throw new Error('[AnalysisGateway] subjectId is required');
        }

        const response = await this.apiClient.get(`${ANALYSIS_GATEWAY_ENDPOINT.SUBJECTS}${normalizedSubjectId}/`, { params });
        return normalizeSubjectResponse(response);
    }

    async createAiJob(payload = {}) {
        const response = await this.apiClient.post(ANALYSIS_GATEWAY_ENDPOINT.AI_JOBS, payload, {
            includeMeta: true
        });
        return normalizeAiJobResponse(response);
    }

    async getAiJob(jobId, params = {}) {
        const normalizedJobId = toTrimmedString(jobId);
        if (!normalizedJobId) {
            throw new Error('[AnalysisGateway] jobId is required');
        }

        const response = await this.apiClient.get(`${ANALYSIS_GATEWAY_ENDPOINT.AI_JOBS}${normalizedJobId}/`, { params });
        return normalizeAiJobResponse(response);
    }

    async unlockCapabilities(payload = {}) {
        const response = await this.apiClient.post(ANALYSIS_GATEWAY_ENDPOINT.CAPABILITY_UNLOCKS, payload);
        return {
            consumptionId: toTrimmedString(response?.consumption_id),
            subjectId: normalizeSubjectId(response?.subject_id),
            requestId: normalizeRequestId(response?.request_id),
            chargedTotal: response?.charged_total ?? null,
            chargedItems: Array.isArray(response?.charged_items) ? response.charged_items : [],
            grantedCapabilities: Array.isArray(response?.granted_capabilities) ? response.granted_capabilities : [],
            fulfillmentStatus: toTrimmedString(response?.fulfillment_status),
            entitlementContext: response?.entitlement_context || null,
            upsellHints: response?.upsell_hints || null,
            raw: response
        };
    }

    async upgradeAiResult(payload = {}) {
        const response = await this.apiClient.post(ANALYSIS_GATEWAY_ENDPOINT.AI_RESULT_UPGRADES, payload);
        const outputLanguage = toTrimmedString(response?.output_language || response?.outputLanguage);
        return {
            executionId: normalizeExecutionId(response?.execution_id),
            subjectId: normalizeSubjectId(response?.subject_id),
            requestId: normalizeRequestId(response?.request_id),
            upgradeState: toTrimmedString(response?.upgrade_state),
            aiDispatchState: toTrimmedString(response?.ai_dispatch_state),
            targetGeneration: response?.target_generation ?? null,
            currentUnlockedGenerationBefore: response?.current_unlocked_generation_before ?? null,
            currentVisibleResultId: toTrimmedString(response?.current_visible_result_id),
            outputLanguage,
            output_language: outputLanguage,
            raw: response
        };
    }
}

export const analysisGateway = new AnalysisGateway();

export default analysisGateway;
